import React, { useEffect, useState, useRef, useCallback } from "react";

/* =========================================================
   ETHIC SPHERE — One-Page Landing
   ========================================================= */

const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [counters, setCounters] = useState({
    factors: 0,
    companies: 0,
    reduction: 0,
  });
  const statsRef = useRef(null);
  const statsAnimated = useRef(false);

  /* ----- Scroll handler: navbar bg + reveal animation ----- */
  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);

    document.querySelectorAll(".reveal:not(.visible)").forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight - 80) el.classList.add("visible");
    });

    /* Animate stat counters once */
    if (!statsAnimated.current && statsRef.current) {
      const rect = statsRef.current.getBoundingClientRect();
      if (rect.top < window.innerHeight - 60) {
        statsAnimated.current = true;
        animateCounters();
      }
    }
  }, []);

  const animateCounters = () => {
    const targets = { factors: 750, companies: 500, reduction: 40 };
    const duration = 2000;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCounters({
        factors: Math.round(targets.factors * ease),
        companies: Math.round(targets.companies * ease),
        reduction: Math.round(targets.reduction * ease),
      });
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  /* ----- Data ----- */
  const problems = [
    {
      icon: "📋",
      title: "ESG Reporting Pressure",
      desc: "Governments like India's BRSR mandate ESG disclosures. Companies struggle to comply with complex, evolving regulations.",
    },
    {
      icon: "🌫️",
      title: "GHG Tracking Gaps",
      desc: "Calculating Scope 1, 2, and 3 emissions accurately is challenging without specialized, reliable systems.",
    },
    {
      icon: "🔍",
      title: "Carbon Offset Opacity",
      desc: "Carbon offset markets lack transparency and traceability, making it hard to verify genuine environmental impact.",
    },
    {
      icon: "📊",
      title: "Inconsistent Data",
      desc: "ESG data is scattered across silos and formats, making it unreliable for investors and stakeholders.",
    },
    {
      icon: "🧩",
      title: "Poor Decision-Making",
      desc: "Without actionable ESG insights, businesses make weak sustainability decisions that cost them long-term.",
    },
  ];

  const solutions = [
    {
      icon: "⚡",
      title: "Automated Data Collection",
      desc: "AI-driven processing collects, validates, and structures ESG data from multiple sources automatically.",
      num: "01",
    },
    {
      icon: "📈",
      title: "ESG Analytics Dashboard",
      desc: "Real-time insights, performance tracking, and visual reports that make complex data easy to act on.",
      num: "02",
    },
    {
      icon: "📄",
      title: "Compliance Reporting",
      desc: "Generate BRSR-ready reports and comply with global ESG frameworks — in just a few clicks.",
      num: "03",
    },
    {
      icon: "🌍",
      title: "GHG Emission Calculation",
      desc: "Accurately track Scope 1, 2, and 3 emissions across your entire operational footprint.",
      num: "04",
    },
    {
      icon: "🔗",
      title: "Blockchain Carbon Offsets",
      desc: "Transparent, verifiable carbon offset tracking using blockchain for full traceability.",
      num: "05",
    },
  ];

  const features = [
    {
      icon: "📊",
      title: "ESG Data Management",
      desc: "750+ ESG factors tracked across environment, social, and governance metrics.",
    },
    {
      icon: "🤖",
      title: "AI-Powered Analytics",
      desc: "Smart gap analysis, predictive insights, and strategy recommendations.",
    },
    {
      icon: "📄",
      title: "Report Generation",
      desc: "BRSR-ready reports with custom templates and automated formatting.",
    },
    {
      icon: "🌱",
      title: "Emission Tracking",
      desc: "Accurate carbon footprint calculation across your entire value chain.",
    },
    {
      icon: "🔗",
      title: "Blockchain Offsets",
      desc: "Transparent, immutable carbon offset tracking on the blockchain.",
    },
    {
      icon: "👤",
      title: "Individual Tracking",
      desc: "Both companies and individuals can monitor and reduce their emissions.",
    },
  ];

  const steps = [
    {
      title: "Data Collection",
      desc: "Input ESG and operational data through our intuitive interface or automated connectors.",
    },
    {
      title: "AI Analysis",
      desc: "Our AI engine processes your data, identifies gaps, and generates actionable insights.",
    },
    {
      title: "Emission Calculation",
      desc: "Automatically calculate your carbon footprint across all emission scopes.",
    },
    {
      title: "Report Generation",
      desc: "Generate compliant ESG & BRSR reports ready for regulatory submission.",
    },
    {
      title: "Carbon Offsetting",
      desc: "Offset your remaining emissions through verified, blockchain-tracked projects.",
    },
    {
      title: "Continuous Monitoring",
      desc: "Track performance over time and continuously improve your sustainability impact.",
    },
  ];

  const benefits = [
    "Ensure regulatory compliance with BRSR & ESG laws",
    "Reduce operational costs through efficiency gains",
    "Improve brand reputation and public trust",
    "Make data-driven business decisions",
    "Increase investor confidence and trust",
    "Improve efficiency across all operations",
    "Strengthen enterprise risk management",
    "Contribute to global sustainability & climate action",
  ];

  const industries = [
    { icon: "🏦", name: "BFSI" },
    { icon: "🏭", name: "Manufacturing" },
    { icon: "🏥", name: "Healthcare" },
    { icon: "⚡", name: "Energy" },
    { icon: "💻", name: "IT & Telecom" },
    { icon: "🏛️", name: "Government" },
  ];

  return (
    <>
      {/* Floating particles */}
      <div className="particles" aria-hidden="true">
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDuration: `${8 + Math.random() * 14}s`,
              animationDelay: `${Math.random() * 10}s`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
            }}
          />
        ))}
      </div>

      {/* -------- NAVBAR -------- */}
      <nav className={`navbar${scrolled ? " scrolled" : ""}`} id="navbar">
        <div className="container">
          <div
            className="nav-logo"
            onClick={() => scrollTo("hero")}
            role="button"
            tabIndex={0}
          >
            {/* <div className="nav-logo-icon">🌍</div> */}

            <img
              src="./images/esg-logo.png" // put your image path here
              alt="Ethic Sphere"
              className="nav-logo-image"
            />
          </div>

          <div className={`nav-links${menuOpen ? " open" : ""}`}>
            <a
              href="#problems"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("problems");
              }}
            >
              Problems
            </a>
            <a
              href="#solution"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("solution");
              }}
            >
              Solution
            </a>
            <a
              href="#features"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("features");
              }}
            >
              Features
            </a>
            <a
              href="#how-it-works"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("how-it-works");
              }}
            >
              How It Works
            </a>
            <a
              href="#benefits"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("benefits");
              }}
            >
              Benefits
            </a>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("contact");
              }}
              className="nav-cta"
            >
              Get Demo
            </a>
          </div>

          <button
            className={`hamburger${menuOpen ? " active" : ""}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      {/* -------- HERO -------- */}
      <section className="hero" id="hero">
        <div className="hero-bg" aria-hidden="true">
          <div className="hero-bg-orb hero-bg-orb--green" />
          <div className="hero-bg-orb hero-bg-orb--cyan" />
          <div className="hero-bg-orb hero-bg-orb--purple" />
          <div className="hero-grid-bg" />
        </div>

        <div className="container hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            AI-Powered ESG Platform
          </div>

          <h1 className="hero-title">
            <span className="hero-title-line">Empowering</span>
            <span className="hero-title-line">
              <span className="gradient-text">Sustainable</span> Business
            </span>
          </h1>

          <p className="hero-desc">
            Track, report, and offset your carbon emissions while ensuring
            regulatory compliance and sustainability — all in one intelligent
            platform.
          </p>

          <div className="hero-actions">
            <a
              href="#contact"
              className="btn-primary"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("contact");
              }}
            >
              Start ESG Reporting →
            </a>
            <a
              href="#how-it-works"
              className="btn-secondary"
              onClick={(e) => {
                e.preventDefault();
                scrollTo("how-it-works");
              }}
            >
              See How It Works
            </a>
          </div>

          <div className="hero-stats" ref={statsRef}>
            <div className="hero-stat">
              <div className="hero-stat-value">{counters.factors}+</div>
              <div className="hero-stat-label">ESG Factors Tracked</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">{counters.companies}+</div>
              <div className="hero-stat-label">Companies Trust Us</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">{counters.reduction}%</div>
              <div className="hero-stat-label">Avg. Emission Reduction</div>
            </div>
          </div>
        </div>
      </section>

      {/* -------- PROBLEMS -------- */}
      <section className="problems" id="problems">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">🚨 The Challenge</span>
            <h2 className="section-title">Why Businesses Struggle with ESG</h2>
            <p className="section-subtitle">
              Companies face mounting pressure to measure, manage, and report
              their environmental and social impact — but the tools aren't
              keeping up.
            </p>
          </div>

          <div className="problems-grid">
            {problems.map((p, i) => (
              <div
                className={`problem-card reveal reveal-delay-${i + 1}`}
                key={i}
              >
                <div className="problem-icon">{p.icon}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------- SOLUTION -------- */}
      <section className="solution" id="solution">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">💡 Our Solution</span>
            <h2 className="section-title">End-to-End ESG Management</h2>
            <p className="section-subtitle">
              Ethic Sphere brings everything together in one powerful platform —
              from data collection to carbon offsetting.
            </p>
          </div>

          <div className="solution-grid">
            {solutions.map((s, i) => (
              <div
                className={`solution-card reveal reveal-delay-${i + 1}`}
                key={i}
              >
                <span className="solution-card-number">{s.num}</span>
                <div className="solution-card-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------- FEATURES -------- */}
      <section className="features" id="features">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">⚙️ Core Features</span>
            <h2 className="section-title">Built for Modern Sustainability</h2>
            <p className="section-subtitle">
              Powerful tools designed to simplify every aspect of ESG management
              — powered by AI and secured by blockchain.
            </p>
          </div>

          <div className="features-grid">
            {features.map((f, i) => (
              <div
                className={`feature-card reveal reveal-delay-${i + 1}`}
                key={i}
              >
                <div className="feature-card-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------- HOW IT WORKS -------- */}
      <section className="how-it-works" id="how-it-works">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">🧩 How It Works</span>
            <h2 className="section-title">Your Journey to Sustainability</h2>
            <p className="section-subtitle">
              Six simple steps from raw data to measurable impact — our platform
              guides you every step of the way.
            </p>
          </div>

          <div className="steps-wrapper">
            <div className="steps-line" aria-hidden="true" />
            {steps.map((s, i) => (
              <div className={`step-item reveal reveal-delay-${i + 1}`} key={i}>
                <div className="step-number">{i + 1}</div>
                <div className="step-content">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------- BENEFITS -------- */}
      <section className="benefits" id="benefits">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">📈 Benefits</span>
            <h2 className="section-title">Real Impact for Your Business</h2>
            <p className="section-subtitle">
              From regulatory compliance to investor confidence — Ethic Sphere
              delivers tangible results.
            </p>
          </div>

          <div className="benefits-grid">
            {benefits.map((b, i) => (
              <div
                className={`benefit-card reveal reveal-delay-${(i % 4) + 1}`}
                key={i}
              >
                <div className="benefit-check">✓</div>
                <p>{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------- INDUSTRIES -------- */}
      <section className="industries" id="industries">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">🏭 Industries</span>
            <h2 className="section-title">Industries We Serve</h2>
            <p className="section-subtitle">
              From financial institutions to government bodies — Ethic Sphere
              adapts to your industry's unique ESG requirements.
            </p>
          </div>

          <div className="industries-grid">
            {industries.map((ind, i) => (
              <div
                className={`industry-card reveal reveal-delay-${i + 1}`}
                key={i}
              >
                <span className="industry-icon">{ind.icon}</span>
                <h4>{ind.name}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------- SECURITY -------- */}
      <section className="security" id="security">
        <div className="container">
          <div className="security-content">
            <div className="security-text reveal">
              <span className="section-label">🔐 Security</span>
              <h2>Enterprise-Grade Data Security</h2>
              <p>
                Your sensitive ESG data is protected by industry-leading
                security measures — so you can focus on sustainability without
                worrying about safety.
              </p>
              <div className="security-features">
                {[
                  { icon: "🔒", label: "End-to-End Data Encryption" },
                  { icon: "🛡️", label: "Complete Data Privacy" },
                  { icon: "✅", label: "Data Integrity Verification" },
                  { icon: "🔗", label: "Third-Party Risk Management" },
                ].map((sf, i) => (
                  <div className="security-feature" key={i}>
                    <div className="security-feature-icon">{sf.icon}</div>
                    <span>{sf.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="security-visual reveal reveal-delay-2">
              <div className="security-shield">
                <div className="shield-outer" />
                <div className="shield-inner">🛡️</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* -------- MARKET OPPORTUNITY -------- */}
      <section className="market" id="market">
        <div className="container">
          <div className="section-header reveal">
            <span className="section-label">📊 Market Opportunity</span>
            <h2 className="section-title">A Massive & Growing Market</h2>
            <p className="section-subtitle">
              The global ESG software market is exploding — and Ethic Sphere is
              positioned right at the center of it.
            </p>
          </div>

          <div className="market-cards reveal">
            <div className="market-card">
              <div className="market-flag">🇮🇳</div>
              <div className="market-value">₹500 Cr</div>
              <div className="market-label">India ESG Market by 2028</div>
            </div>
            <div className="market-card">
              <div className="market-flag">🌍</div>
              <div className="market-value">$940M</div>
              <div className="market-label">Global Market by 2030</div>
            </div>
          </div>

          <div className="market-trends reveal reveal-delay-2">
            <div className="market-trend">
              <div className="market-trend-icon">📈</div>
              <p>Rising demand for ESG compliance solutions worldwide</p>
            </div>
            <div className="market-trend">
              <div className="market-trend-icon">🌿</div>
              <p>Growing need for verified carbon offset solutions</p>
            </div>
            <div className="market-trend">
              <div className="market-trend-icon">🔍</div>
              <p>Investors demanding greater transparency in ESG data</p>
            </div>
          </div>
        </div>
      </section>

      {/* -------- CTA -------- */}
      <section className="cta-section" id="contact">
        <div className="container">
          <div className="cta-box reveal">
            <h2>Ready to Transform Your ESG Strategy?</h2>
            <p>
              Join hundreds of forward-thinking companies already using Ethic
              Sphere to drive sustainable, compliant, and transparent business
              growth.
            </p>
            <div className="hero-actions">
              <a href="mailto:chinmay@ethicsphere.com" className="btn-primary">
                Request a Demo →
              </a>
              <a href="mailto:chinmay@ethicsphere.com" className="btn-secondary">
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* -------- FOOTER -------- */}
      <footer className="footer" id="footer">
        <div className="container">
          <div className="footer-content">
            <div
              className="nav-logo"
              onClick={() => scrollTo("hero")}
              role="button"
              tabIndex={0}
            >
              {/* <div className="nav-logo-icon">🌍</div> */}

              <img
                src="./images/esg-logo.png" // put your image path here
                alt="Ethic Sphere"
                className="nav-logo-image"
              />
            </div>

            <div className="footer-links">
              <a
                href="#problems"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("problems");
                }}
              >
                Problems
              </a>
              <a
                href="#solution"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("solution");
                }}
              >
                Solution
              </a>
              <a
                href="#features"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("features");
                }}
              >
                Features
              </a>
              <a
                href="#benefits"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("benefits");
                }}
              >
                Benefits
              </a>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("contact");
                }}
              >
                Contact
              </a>
            </div>

            <div className="footer-copy">
              © {new Date().getFullYear()} EthicsSphere. All rights reserved. |
              Founded by Chinmay Shah
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Home;
